import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight,
  Database,
  Brain,
  Target,
  BarChart3,
  Zap,
  CheckCircle2,
  Layers,
  GitBranch,
  Radio,
  PieChart,
  TrendingUp,
  Clock,
  Globe,
  Lock,
  Users,
  Shield,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import "../App.css";
import "./HowItWorks.css";


// Hero Section
const HowItWorksHero = () => {
  return (
    <section className="hiw-hero">
      <div className="hiw-hero-content">
        <span className="hiw-badge">HOW IT WORKS</span>
        <h1>Intelligence-Driven Marketing That Delivers Results</h1>
        <p>
          Discover how IntentLogic combines proprietary consumer data, predictive AI, 
          and expert campaign management to drive measurable business outcomes.
        </p>
        <div className="hiw-hero-cta">
          <Button 
            className="btn-primary" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Schedule a Demo
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Process Steps Data
const processSteps = [
  {
    number: "01",
    title: "Data Integration",
    subtitle: "Connect Your Goals",
    description: "We begin by understanding your business objectives and target audience. Our team works with you to define success metrics and identify the ideal customer profiles you want to reach.",
    icon: Database,
    details: [
      "Define campaign objectives and KPIs",
      "Identify target customer characteristics",
      "Set up measurement frameworks",
      "Establish baseline metrics"
    ]
  },
  {
    number: "02",
    title: "Audience Intelligence",
    subtitle: "Leverage Our Data",
    description: "We tap into our proprietary database of 245M+ consumer profiles, enriched with behavioral signals, intent data, and predictive scores to build your ideal audience segments.",
    icon: Brain,
    details: [
      "Access 245M+ consumer profiles",
      "Apply behavioral and intent signals",
      "Build custom propensity models",
      "Create lookalike audiences at scale"
    ]
  },
  {
    number: "03",
    title: "Predictive Targeting",
    subtitle: "AI-Powered Selection",
    description: "Our IRIS platform uses machine learning algorithms to score and rank prospects based on their likelihood to convert, ensuring your marketing reaches the highest-potential audiences.",
    icon: Target,
    details: [
      "Machine learning propensity scoring",
      "Real-time intent signal processing",
      "Cross-channel identity resolution",
      "Continuous model optimization"
    ]
  },
  {
    number: "04",
    title: "Campaign Activation",
    subtitle: "Multi-Channel Execution",
    description: "We activate your audiences across all major marketing channels—programmatic, social, email, direct mail, and more—with consistent messaging and coordinated timing.",
    icon: Zap,
    details: [
      "Programmatic display and video",
      "Social media platforms",
      "Email and direct mail",
      "Connected TV and audio"
    ]
  },
  {
    number: "05",
    title: "Performance Optimization",
    subtitle: "Continuous Improvement",
    description: "Our analytics team monitors campaign performance in real-time, making data-driven optimizations to maximize ROI and achieve your business objectives.",
    icon: BarChart3,
    details: [
      "Real-time performance monitoring",
      "A/B testing and optimization",
      "Budget allocation adjustments",
      "Creative performance analysis"
    ]
  },
  {
    number: "06",
    title: "Results & Insights",
    subtitle: "Measure What Matters",
    description: "We provide comprehensive reporting on campaign performance, attribution analysis, and actionable insights to inform future strategies and demonstrate clear ROI.",
    icon: TrendingUp,
    details: [
      "Multi-touch attribution",
      "Incrementality measurement",
      "Customer lifetime value analysis",
      "Strategic recommendations"
    ]
  }
];

// Platform Capabilities
const platformCapabilities = [
  {
    icon: Users,
    title: "245M+ Consumer Profiles",
    description: "Access one of the largest consumer databases in the industry, with comprehensive demographic, behavioral, and transactional data."
  },
  {
    icon: Brain,
    title: "AI-Powered Intelligence",
    description: "Our machine learning models continuously analyze patterns and predict consumer behavior with industry-leading accuracy."
  },
  {
    icon: Globe,
    title: "Cross-Channel Activation",
    description: "Reach your audiences wherever they are—across digital, social, email, direct mail, and emerging channels."
  },
  {
    icon: Clock,
    title: "Real-Time Optimization",
    description: "Campaigns are monitored and optimized continuously, ensuring maximum performance throughout the lifecycle."
  },
  {
    icon: Shield,
    title: "Privacy-First Approach",
    description: "All data is collected and used in compliance with privacy regulations, with robust security measures in place."
  },
  {
    icon: PieChart,
    title: "Transparent Measurement",
    description: "Clear, honest reporting on what's working and what isn't, with true incrementality measurement."
  }
];

// Differentiators
const differentiators = [
  {
    title: "We Execute, You Succeed",
    description: "Unlike self-serve platforms, we handle the complexity. Our expert team manages your campaigns end-to-end, so you can focus on your business.",
    icon: Layers
  },
  {
    title: "Proprietary Data Advantage",
    description: "Our first-party data asset gives you access to consumer intelligence that simply isn't available through other platforms or data providers.",
    icon: Database
  },
  {
    title: "Predictive, Not Reactive",
    description: "We identify consumers who are about to make decisions, not those who already have. This forward-looking approach drives better results.",
    icon: GitBranch
  },
  {
    title: "True Performance Focus",
    description: "We're measured on your business outcomes, not vanity metrics. Our success is tied directly to driving real, measurable results.",
    icon: Target
  }
];

// Timeline visual component
const ProcessTimeline = () => {
  return (
    <div className="process-timeline">
      {processSteps.map((step, index) => (
        <div key={index} className="timeline-step">
          <div className="timeline-connector">
            <div className="timeline-number">{step.number}</div>
            {index < processSteps.length - 1 && <div className="timeline-line" />}
          </div>
          <div className="timeline-content">
            <div className="timeline-icon">
              <step.icon size={28} />
            </div>
            <div className="timeline-text">
              <span className="timeline-subtitle">{step.subtitle}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              <ul className="timeline-details">
                {step.details.map((detail, idx) => (
                  <li key={idx}>
                    <CheckCircle2 size={16} />
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Component
const HowItWorksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="how-it-works-page">
        <SEO 
          title="How It Works - Our Process & Methodology"
          description="Discover how IntentLogic combines proprietary consumer data, predictive AI, and expert campaign management to drive measurable business outcomes. Learn our proven methodology."
          keywords="how it works, marketing process, data methodology, AI marketing, campaign management, marketing strategy, performance marketing process"
          canonical="/how-it-works"
        />
        
        {/* Hero Section */}
        <HowItWorksHero />

        {/* Overview Section */}
        <section className="hiw-overview">
          <div className="hiw-overview-content">
            <h2>The IntentLogic Difference</h2>
            <p className="hiw-overview-text">
              We're not a self-serve platform or a simple data provider. IntentLogic is a 
              full-service marketing intelligence partner that combines proprietary data, 
              advanced technology, and expert execution to deliver campaigns that actually work.
            </p>
            <div className="hiw-overview-grid">
              {differentiators.map((item, index) => (
                <div key={index} className="hiw-differentiator">
                  <div className="hiw-diff-icon">
                    <item.icon size={24} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="hiw-process">
          <div className="hiw-process-header">
            <span className="hiw-section-label">THE PROCESS</span>
            <h2>From Strategy to Results in Six Steps</h2>
            <p>
              Our proven methodology ensures every campaign is built on solid strategy, 
              powered by data, and optimized for maximum performance.
            </p>
          </div>
          <ProcessTimeline />
        </section>

        {/* Platform Capabilities */}
        <section className="hiw-capabilities">
          <div className="hiw-capabilities-header">
            <span className="hiw-section-label">PLATFORM CAPABILITIES</span>
            <h2>The Technology Behind Your Success</h2>
            <p>
              Our IRIS platform combines cutting-edge technology with years of marketing 
              intelligence to deliver capabilities you won't find anywhere else.
            </p>
          </div>
          <div className="hiw-capabilities-grid">
            {platformCapabilities.map((cap, index) => (
              <div key={index} className="hiw-capability-card">
                <div className="hiw-cap-icon">
                  <cap.icon size={28} />
                </div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>
      </section>

      {/* Data Section */}
      <section className="hiw-data">
        <div className="hiw-data-content">
          <div className="hiw-data-text">
            <span className="hiw-section-label">OUR DATA</span>
            <h2>Proprietary Intelligence at Scale</h2>
            <p>
              Our consumer database is one of the largest and most comprehensive in the 
              industry. Built over years through first-party data partnerships and 
              proprietary collection methods, it provides insights you simply can't get 
              elsewhere.
            </p>
            <div className="hiw-data-stats">
              <div className="hiw-data-stat">
                <span className="stat-number">245M+</span>
                <span className="stat-label">Consumer Profiles</span>
              </div>
              <div className="hiw-data-stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Data Attributes</span>
              </div>
              <div className="hiw-data-stat">
                <span className="stat-number">Real-Time</span>
                <span className="stat-label">Intent Signals</span>
              </div>
            </div>
            <ul className="hiw-data-features">
              <li>
                <CheckCircle2 size={20} />
                <span>Demographic & firmographic data</span>
              </li>
              <li>
                <CheckCircle2 size={20} />
                <span>Behavioral and transactional signals</span>
              </li>
              <li>
                <CheckCircle2 size={20} />
                <span>In-market intent indicators</span>
              </li>
              <li>
                <CheckCircle2 size={20} />
                <span>Cross-device identity resolution</span>
              </li>
              <li>
                <CheckCircle2 size={20} />
                <span>Predictive propensity scores</span>
              </li>
            </ul>
          </div>
          <div className="hiw-data-visual">
            <div className="data-viz-container">
              <div className="data-viz-ring ring-1">
                <div className="ring-label">Consumer Profiles</div>
              </div>
              <div className="data-viz-ring ring-2">
                <div className="ring-label">Behavioral Data</div>
              </div>
              <div className="data-viz-ring ring-3">
                <div className="ring-label">Intent Signals</div>
              </div>
              <div className="data-viz-center">
                <Database size={32} />
                <span>IRIS Platform</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="hiw-use-cases">
        <div className="hiw-use-cases-header">
          <span className="hiw-section-label">USE CASES</span>
          <h2>Solutions for Every Marketing Challenge</h2>
        </div>
        <div className="hiw-use-cases-grid">
          <div className="hiw-use-case">
            <h3>Customer Acquisition</h3>
            <p>Find and convert high-value prospects with precision targeting and optimized messaging.</p>
            <Link to="/customer-acquisition" className="use-case-link">
              Learn More <ChevronRight size={16} />
            </Link>
          </div>
          <div className="hiw-use-case">
            <h3>Customer Activation</h3>
            <p>Re-engage lapsed customers and drive repeat purchases with personalized campaigns.</p>
            <Link to="/customer-activation" className="use-case-link">
              Learn More <ChevronRight size={16} />
            </Link>
          </div>
          <div className="hiw-use-case">
            <h3>Political & Advocacy</h3>
            <p>Reach voters and supporters with compliant, effective outreach at scale.</p>
            <Link to="/political-advocacy" className="use-case-link">
              Learn More <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="hiw-faq">
        <div className="hiw-faq-header">
          <span className="hiw-section-label">FAQ</span>
          <h2>Common Questions</h2>
        </div>
        <div className="hiw-faq-grid">
          <div className="hiw-faq-item">
            <h3>How long does it take to launch a campaign?</h3>
            <p>Most campaigns can be live within 2-3 weeks from kickoff. This includes audience development, creative strategy, and technical setup. Rush timelines are available for time-sensitive initiatives.</p>
          </div>
          <div className="hiw-faq-item">
            <h3>Do we need to provide our own data?</h3>
            <p>No. While we can incorporate your first-party data for even better results, our proprietary database of 245M+ consumers provides everything needed to build high-performing audiences.</p>
          </div>
          <div className="hiw-faq-item">
            <h3>What channels can you activate?</h3>
            <p>We support all major digital channels including programmatic display/video, social media, email, direct mail, connected TV, streaming audio, and more. We'll recommend the right mix for your goals.</p>
          </div>
          <div className="hiw-faq-item">
            <h3>How do you measure success?</h3>
            <p>We focus on business outcomes, not vanity metrics. This includes conversion tracking, multi-touch attribution, incrementality testing, and customer lifetime value analysis.</p>
          </div>
          <div className="hiw-faq-item">
            <h3>What industries do you work with?</h3>
            <p>We work across financial services, insurance, retail, healthcare, education, political/advocacy, and more. Our data and technology are adaptable to virtually any B2C marketing challenge.</p>
          </div>
          <div className="hiw-faq-item">
            <h3>How is data privacy handled?</h3>
            <p>All data is collected and used in compliance with applicable privacy regulations including CCPA and state privacy laws. We maintain rigorous security standards and honor all consumer opt-out requests.</p>
          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="hiw-cta">
          <div className="hiw-cta-content">
            <h2>Ready to See IntentLogic in Action?</h2>
            <p>
              Schedule a consultation with our team to discuss your marketing challenges 
              and see how our platform can drive measurable results for your business.
            </p>
            <Button 
              className="btn-primary btn-large" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Schedule Consultation
              <ArrowRight size={18} />
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HowItWorksPage;
